import { DateTimeLocalizationInfo } from '@mews-framework/globalization';
import {
    createCalendarService as createFrameworkCalendarService,
    padStartZero,
    StructuredDate,
} from '@optimus-web/core';
import { AppClockService } from '../appClock';
import { DateService } from '../dates';
import { DateTimeService } from '../dateTimes';
import * as DateTimeUtils from '../dateTimeUtils';
import { ISOFormat } from '../enums';
import { WallClockDateService } from '../wallClockDates';

export const createCalendarService = ({
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getCurrentDateTimeLocalizationInfo,
    appClockService,
}: {
    getCurrentEnterpriseIanaTimeZoneIdentifier: () => string;
    getCurrentDateTimeLocalizationInfo: () => DateTimeLocalizationInfo;
    appClockService: AppClockService;
}) => {
    const getTimezone = () => getCurrentEnterpriseIanaTimeZoneIdentifier();
    const getLocalization = () => getCurrentDateTimeLocalizationInfo();

    const isValidDateString = (dateString: string) => DateService.isValidString(dateString, getLocalization());
    const getCurrentDate = () => {
        const todayDate = DateTimeService.startOfDay(
            DateTimeService.fromUtc(appClockService.getAppNowUtc(), getTimezone())
        );
        return DateTimeUtils.dateTimeToStructuredDate(todayDate);
    };
    const fromDateString = (dateString: string) => {
        const date = DateService.fromString(dateString, getLocalization(), getTimezone());
        return DateTimeUtils.dateToStructuredDate(date);
    };
    const getISODateString = ({ year, month, date }: StructuredDate) =>
        `${padStartZero(year, 4)}-${padStartZero(month + 1)}-${padStartZero(date)}`;

    const getDateString = (date: StructuredDate) => {
        const dateStringISO = getISODateString(date);
        const dsDate = DateService.fromString(dateStringISO, ISOFormat.Date);
        return DateService.toString(dsDate, getLocalization());
    };

    const getFormattedDateString = (date: StructuredDate, dateFormat: string) => {
        const ISODateString = getISODateString(date);

        return WallClockDateService.toString(WallClockDateService.parse(ISODateString), {
            ...getLocalization(),
            dateFormat,
        });
    };

    return createFrameworkCalendarService({
        getLocalization,
        isValidDateString,
        getCurrentDate,
        fromDateString,
        getDateString,
        getFormattedDateString,
    });
};
